import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ParticleBackground } from "@/components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

const faqs = [
  {
    question: "What are Samboards?",
    answer: "Samboards are premium tactile sensory boards designed to reduce anxiety, manage stress, and improve focus. They help with Sensory Activated motor-skills and are used by families, therapists, schools, and professionals."
  },
  {
    question: "Who can benefit from using Samboards?",
    answer: "Samboards are beneficial for people living with ADHD, autism, anxiety, mental health concerns, dementia, stroke recovery, and anyone seeking sensory support or stress relief."
  },
  {
    question: "How do I use a Samboard?",
    answer: "Simply touch and feel the different textures on the board. The tactile engagement exercises your hands, increases messages to your brain, and releases natural chemicals that can help you feel motivated and improve your sense of well-being."
  },
  {
    question: "Are Samboards suitable for children?",
    answer: "Yes, Samboards are suitable for all ages, including children. They're used in schools and by families to help children with focus, stress management, and sensory needs."
  },
  {
    question: "What's the difference between the products?",
    answer: "We offer various sizes and formats: Full-size Samboards for home and clinical settings, Library Squares for quiet environments, Sensory Pads for softer textures, and Fidget Squares for pockets. An Equestrian range is in development."
  },
  {
    question: "Do you offer bulk orders for schools or therapists?",
    answer: "Yes! We work with schools, therapists, and healthcare providers. Please contact us or visit our 'For Professionals' page for information about institutional partnerships and bulk pricing."
  },
  {
    question: "How do I clean my Samboard?",
    answer: "Most Samboards can be wiped clean with a damp cloth. Sensory Pads are machine washable. Always check the care instructions included with your specific product."
  },
  {
    question: "Where are Samboards made?",
    answer: "All Samboards are designed and manufactured in the United Kingdom, ensuring high quality standards and supporting local communities."
  },
  {
    question: "What is your returns policy?",
    answer: "We want you to be completely satisfied with your purchase. Please contact us if you have any concerns about your order, and we'll work with you to find a solution."
  },
  {
    question: "How long does delivery take?",
    answer: "UK delivery typically takes 3-5 business days. We'll send you tracking information once your order is dispatched."
  }
];

export default function FAQ() {
  return (
    <>
      <SEOHead
        title="FAQ | Frequently Asked Questions | Samboards"
        description="Find answers to common questions about Samboards tactile sensory tools. Learn about product benefits, usage, care instructions, bulk orders & UK delivery."
        canonical="https://samboards.co.uk/faq"
        keywords="Samboards FAQ, sensory tools questions, tactile board information, ADHD tools FAQ, autism support FAQ, SEN resources FAQ, UK delivery, bulk orders"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "FAQ", url: "/faq" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />
      
      <Page>
        <div className="pb-24">
          <div className="text-center mb-16">
            <h1 className="text-[40px] sm:text-[48px] lg:text-[54px] leading-[1.1] font-bold mb-4 page-title-gradient">Frequently Asked Questions</h1>
            <p className="text-xl text-slate-200/80">
              Find answers to common questions about Samboards
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 px-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              >
                <AccordionTrigger className="text-left font-semibold text-slate-100 hover:text-emerald-400">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-300/70 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mt-16 text-center space-y-4">
            <h2 className="text-2xl font-semibold text-slate-100">Still have questions?</h2>
            <p className="text-slate-300/70">
              We're here to help. Get in touch with our team.
            </p>
            <a 
              href="/contact" 
              className="inline-block text-emerald-400 font-medium hover:text-emerald-300 text-lg transition-colors"
            >
              Contact us →
            </a>
          </div>
        </div>
      </Page>
    </div>
    </>
  );
}
